import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { Input } from 'components/ui/Input'
import { useFormik } from 'formik'
import { formFieldProps } from 'utils/form'
import { createValidationSchema, required } from 'utils/validations'
import { UIButton } from 'components/ui/Button'
import { KeyboardEvent, useState } from 'react'
import { registerUser } from 'api/auth'
import { assignGuestChatsToCurrentUser, getMyChats } from 'api/chats'
import { InputPassword } from 'components/ui/InputPassword'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import { useNavigate } from 'react-router'
import { getUserNameSurname } from 'utils/user'
import { hideModal, showModal } from '../../managers/modal'
import { LoginModal } from 'components/modals/LoginModal'

type Props = {
  onSuccess?: () => void
}

export type RegisterForm = {
  email: string
  password: string
  name: string
  surname: string
}

export const RegisterModal = ({ onSuccess }: Props) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const { values, errors, setFieldValue, setFieldError, handleSubmit } = useFormik<RegisterForm>({
    initialValues: {
      email: '',
      password: '',
      name: '',
      surname: '',
    },
    validationSchema: createValidationSchema({
      email: required,
      password: required,
      name: required,
      surname: required,
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      console.debug({ values })
      try {
        setLoading(true)
        const user = await registerUser(values)
        await assignGuestChatsToCurrentUser()
        await getMyChats({})
        hideModal()
        onSuccess?.()
        const nameSurname = getUserNameSurname(user)
        showSuccessToast(`${t('welcomeLogin')} ${nameSurname}`)
      } catch (e) {
        console.error({ e })
        if (e === 'userNotAllowedToLogin') {
          showErrorToast(t('userNotAllowedToLogin'))
        } else {
          showErrorToast(t('loginError'))
        }
      } finally {
        setLoading(false)
      }
    },
  })

  const onClickAlreadyRegistered = () => {
    hideModal()
    showModal({
      children: <LoginModal />,
    })
  }

  const fieldProps = formFieldProps(errors, values, setFieldError, setFieldValue)

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <div className={'tw-flex tw-flex-row tw-items-center tw-justify-between'}>
        <div className={'tw-font-[800]'}>{t('register')}</div>
        <div className={'tw-cursor-pointer'}>
          <FontAwesomeIcon icon={['far', 'times']} onClick={() => navigate(-1)} />
        </div>
      </div>
      <div className={'tw-flex tw-flex-col tw-space-y-1 tw-mt-2'}>
        <Input label={t('name')} {...fieldProps('name')} onKeyDown={onKeyDown} />
        <Input label={t('surname')} {...fieldProps('surname')} onKeyDown={onKeyDown} />
        <Input label={t('email')} {...fieldProps('email')} onKeyDown={onKeyDown} />
        <InputPassword label={t('password')} {...fieldProps('password')} onKeyDown={onKeyDown} />
        <div className={'tw-h-2'} />
        <div className={'tw-text-sm tw-underline tw-cursor-pointer'} onClick={onClickAlreadyRegistered}>
          {t('alreadyRegistered')}
        </div>
      </div>
      <div className={'tw-mt-6 tw-w-full'}>
        <UIButton className={'tw-w-full'} onClick={() => handleSubmit()} loading={loading}>
          <span className={'tw-text-sm'}>{t('register')}</span>
        </UIButton>
      </div>
    </div>
  )
}
